<template>
  <c-loading-area :loading="loading" class="nav-tab-medium">
    <template #default>
      <v-card-title>
        {{ $t('securedCaseAccess.title') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('page.admin.userAccount.securedCaseAccess.description') }}
      </v-card-subtitle>
      <v-card-text>
        <mini-card v-if="organization !== null" class="mt-4">
          <v-card-text>
            <div class="d-flex pa-2" style="gap:20px">
              <div class="flex-grow-0">
                <v-icon color="primary" style="font-size:50px">
                  mdi-office-building
                </v-icon>
              </div>
              <v-divider vertical/>
              <div class="flex-grow-1" style="max-width:350px">
                <div style="font-size:1.5rem">
                  {{ $t('page.admin.userAccount.securedCaseAccess.orgAccess.title') }}
                </div>
                <div class="text-minimum-gray mt-2">
                  {{ $t('page.admin.userAccount.securedCaseAccess.orgAccess.description') }}
                </div>
                <div class="mt-6">
                  <router-link :to="{ name: 'manage-org-secured-case-access', params: { organizationID: organization.resourceID } }"
                               target="_blank">
                    {{ organization.sortName }}
                  </router-link>
                </div>
              </div>
            </div>
          </v-card-text>
        </mini-card>

        <mini-card class="mt-4 mb-8">
          <v-card-text>
            <div class="d-flex pa-2" style="gap:20px">
              <div class="flex-grow-0">
                <v-icon color="primary" style="font-size:50px">
                  mdi-shield-check
                </v-icon>
              </div>
              <v-divider vertical/>
              <div class="flex-grow-1" style="max-width:350px">
                <div style="font-size:1.5rem">
                  {{ $t('securedCaseAccess.grantAccess') }}
                </div>
                <div class="text-minimum-gray mt-2">
                  {{ $t('page.admin.userAccount.securedCaseAccess.grantAccess.description') }}
                </div>
                <div class="mt-6">
                  <span style="font-size:1.05rem">{{ $t('securedCaseAccess.public') }}</span>
                  <c-checkbox v-model="verifiedPublic"
                              :label="$t('securedCaseAccess.elevateToVerified')"
                              class="ml-7 mt-0"
                              hide-details="auto"/>
                  <v-divider v-if="securityTypes.length > 0" class="my-3"/>
                  <template v-for="(selection) in selections">
                    <div :key="selection.securityTypeID" class="mt-3">
                      <c-checkbox v-model="selection.isSelected"
                                  :label="selection.name"
                                  class="mt-0"
                                  @input="(val) => { if (!val) { selection.isVerified = false; } }"
                                  hide-details="auto"/>
                      <c-checkbox v-model="selection.isVerified"
                                  :label="$t('securedCaseAccess.elevateToVerified')"
                                  class="ml-7 mt-0"
                                  :disabled="!selection.isSelected"
                                  hide-details="auto"/>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="justify-end mt-8">
            <c-btn type="primary" @click="save">
              {{ $t('global.save') }}
            </c-btn>
          </v-card-actions>
        </mini-card>
      </v-card-text>
    </template>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import MiniCard from '@components/support/MiniCard';

export default {
  components: { MiniCard },
  props: {
    userID: {
      type: String
    }
  },
  data () {
    return {
      loading: true,
      organization: null,
      verifiedPublic: false, // track this one separate from the concrete security types in `selections` since it can't be keyed properly
      initialMappings: [], // holds the org's initial persisted data to seed the form
      securityTypes: [], // master list of the cms case security types
      selections: [] // stores the current form values
    };
  },
  created () {
    let promises = [];
    promises.push(this.loadOrganization());
    promises.push(this.loadSecurityTypes());
    promises.push(this.loadCurrentPermissions());

    Promise.all(promises)
      .then(this.initalizeForm)
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    initalizeForm () {
      // stub the 'selections' mapping out with each security type
      this.securityTypes.forEach((st) => {
        let selection = {
          securityTypeID: st.securityTypeID,
          name: st.securityTypeName,
          isSelected: false,
          isVerified: false
        };
        this.selections.push(selection);
      });

      // then apply any of the user's current permissions, ie load the data
      this.initialMappings.forEach((bean) => {
        let securityTypeID = bean.caseSecurityTypeExternalIdentifier;
        if (_.isNil(securityTypeID))
        {
          if (bean.verifiedCaseParty)
          {
            this.verifiedPublic = true;
          }
        }
        else
        {
          let selection = _.find(this.selections, { securityTypeID });
          if (!_.isNil(selection))
          {
            selection.isSelected = true;
            selection.isVerified = _.defaultTo(bean.verifiedCaseParty, false);
          }
        }
      });
    },
    loadOrganization () {
      let url = this.$cove.getAPI({
        name: 'api.user',
        params: {
          userID: this.userID
        },
        query: {
          fields: '*,organization(*,organizationDetails(*)) '
        }
      });

      return axios.get(url)
        .then((response) => {
          this.organization = _.defaultTo(response.data.organization, null);
        });
    },
    loadSecurityTypes () {
      let url = this.$cove.getAPI({ name: 'api.securitytypes.case' });
      return axios.get(url)
        .then((response) => {
          this.securityTypes = _.defaultTo(response.data, []);
        });
    },
    loadCurrentPermissions () {
      let url = this.$cove.getAPI({ name: 'api.user.securityTypeMap', params: { userID: this.userID } });
      return axios.get(url)
        .then((response) => {
          this.initialMappings = _.defaultTo(response.data, []);
        });
    },
    save () {
      this.$cove.blockNow();

      let payload = {
        userCaseSecurityMapRequests: []
      };

      if (this.verifiedPublic)
      {
        payload.userCaseSecurityMapRequests.push({
          caseSecurityTypeExternalIdentifier: null,
          verifiedCasePartyFlag: true
        });
      }

      _.forEach(this.selections, (selection) => {
        if (selection.isSelected)
        {
          payload.userCaseSecurityMapRequests.push({
            caseSecurityTypeExternalIdentifier: selection.securityTypeID,
            verifiedCasePartyFlag: selection.isVerified
          });
        }
      });

      let url = this.$cove.getAPI({ name: 'api.user.securityTypeMap', params: { userID: this.userID } });
      axios.patch(url, payload)
        .then(() => {
          this.$cove.notify({ message: this.$t('application.save.success'), color: 'success' });
        })
        .finally(() => {
          this.$cove.unblock();
        });
    }
  }
};
</script>